import { useReducedMotion } from "framer-motion";
import { Reveal, SectionTag, MaskedLines } from "@/components/Reveal";
import { GoldDot, DrawLine, FadeG, SvgLabel, TravelDot } from "@/components/InsightVisuals";

/* technology architecture: experience layer -> platforms -> systems -> infrastructure */
const TechnologyArchitecture = ({ reduce }) => {
  const systems = [
    { label: "CRM & Workflows", x: 62 },
    { label: "Operations Systems", x: 171 },
    { label: "Payment Integrations", x: 280 },
    { label: "Communication Systems", x: 389 },
    { label: "Data & Reporting", x: 498 },
  ];
  return (
    <div data-testid="technology-architecture" className="relative" aria-hidden="true">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/[0.06] blur-[100px]"
        aria-hidden="true"
      />
      <svg viewBox="0 0 560 420" className="relative mx-auto w-full max-w-2xl">
        <g stroke="rgba(255,255,255,0.03)" strokeWidth="1">
          {[112, 224, 336, 448].map((v) => (
            <g key={v}>
              <line x1={v} y1="0" x2={v} y2="420" />
              <line x1="0" y1={v} x2="560" y2={v} />
            </g>
          ))}
        </g>

        {/* experience layer */}
        <FadeG delay={0.2} reduce={reduce}>
          <rect x="180" y="22" width="200" height="34" rx="4" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
        </FadeG>
        <SvgLabel x="280" y="43" size={9.5} delay={0.4} reduce={reduce}>Customer Experience</SvgLabel>
        <DrawLine x1="280" y1="56" x2="280" y2="88" delay={0.55} reduce={reduce} stroke="rgba(212,175,55,0.45)" />

        {/* platforms layer */}
        <FadeG delay={0.7} reduce={reduce}>
          <rect x="170" y="90" width="220" height="34" rx="4" fill="rgba(212,175,55,0.06)" stroke="rgba(212,175,55,0.45)" strokeWidth="1" />
        </FadeG>
        <SvgLabel x="280" y="111" gold size={9.5} delay={0.9} reduce={reduce}>Customer Platforms</SvgLabel>

        {/* systems fan */}
        {systems.map((s, i) => (
          <g key={s.label}>
            <DrawLine x1="280" y1="124" x2={s.x} y2="180" delay={1 + i * 0.14} reduce={reduce} stroke="rgba(255,255,255,0.13)" />
            <GoldDot cx={s.x} cy={184} r={4} delay={1.15 + i * 0.14} reduce={reduce} />
            <SvgLabel x={s.x} y={i % 2 === 0 ? 208 : 226} size={7.5} delay={1.3 + i * 0.14} reduce={reduce}>
              {s.label}
            </SvgLabel>
            <DrawLine x1={s.x} y1="240" x2={s.x} y2="288" delay={1.7 + i * 0.12} reduce={reduce} stroke="rgba(212,175,55,0.25)" />
          </g>
        ))}

        {/* infrastructure base */}
        <FadeG delay={2.2} reduce={reduce}>
          <rect x="40" y="290" width="480" height="40" rx="4" fill="rgba(212,175,55,0.08)" stroke="rgba(212,175,55,0.5)" strokeWidth="1" />
        </FadeG>
        <SvgLabel x="280" y="314" gold size={10} delay={2.4} reduce={reduce}>Business Infrastructure</SvgLabel>
        <TravelDot points={[[280, 39], [280, 107], [389, 184], [389, 288], [280, 310]]} reduce={reduce} duration={11} />
      </svg>
    </div>
  );
};

export default function Technology() {
  const reduce = useReducedMotion();
  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="scroll-mt-20 border-t border-white/5 bg-[#0C0B08] py-20 sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionTag index="08">Technology</SectionTag>
            </Reveal>
            <h2
              data-testid="technology-headline"
              className="mt-6 font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
            >
              <MaskedLines delay={0.1}>
                Infrastructure for
                <br />
                <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
                  scalable service operations.
                </span>
              </MaskedLines>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-xl self-end text-base font-normal leading-relaxed text-slate-300 sm:text-lg lg:ml-auto">
              Technology enables our businesses to manage customer journeys,
              operational workflows, service networks, communication, payments
              and performance with greater structure and visibility.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14">
            <TechnologyArchitecture reduce={reduce} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
